import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { FlatSearch } from '../types/api';
import { Badge, Spinner } from './index';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { api } from '../services/api';
import { trackEvent } from '../services/analytics';

interface SearchSwitcherProps {
  searches: FlatSearch[];
  selectedSearch: FlatSearch | null;
  loading: boolean;
  onSelect: (searchId: string) => void;
  onCreateSearch?: () => void;
  onSearchJoined?: (search: FlatSearch) => void;
}

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'default'> = {
  active: 'success',
  completed: 'warning',
  archived: 'default',
};

export function SearchSwitcher({ searches, selectedSearch, loading, onSelect, onCreateSearch, onSearchJoined }: SearchSwitcherProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [showJoinInput, setShowJoinInput] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const closeDropdown = useCallback(() => {
    setOpen(false);
    setFocusedIndex(-1);
    setShowJoinInput(false);
    setInviteCode('');
    setJoinError('');
    triggerRef.current?.focus();
  }, []);

  const openDropdown = useCallback(() => {
    setOpen(true);
    optionRefs.current = optionRefs.current.slice(0, searches.length);
    const selectedIdx = searches.findIndex((s) => s.id === selectedSearch?.id);
    setFocusedIndex(selectedIdx >= 0 ? selectedIdx : 0);
  }, [searches, selectedSearch]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        closeDropdown();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [closeDropdown]);

  useEffect(() => {
    if (open && focusedIndex >= 0 && optionRefs.current[focusedIndex]) {
      optionRefs.current[focusedIndex]?.focus();
    }
  }, [open, focusedIndex]);

  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) {
        openDropdown();
      }
    } else if (e.key === 'Escape') {
      closeDropdown();
    }
  };

  const handleOptionKeyDown = (e: React.KeyboardEvent, index: number) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((prev) => (prev < searches.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prev) => (prev > 0 ? prev - 1 : searches.length - 1));
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        onSelect(searches[index].id);
        closeDropdown();
        break;
      case 'Escape':
        e.preventDefault();
        closeDropdown();
        break;
    }
  };

  const handleJoin = async () => {
    const code = inviteCode.trim();
    if (!code) return;
    setJoining(true);
    setJoinError('');
    try {
      const search = await api.joinSearch(code);
      setInviteCode('');
      setShowJoinInput(false);
      closeDropdown();
      trackEvent('partner_joined', { search_id: search.id });
      onSearchJoined?.(search);
    } catch {
      setJoinError(t('app.searchSwitcher.failedToJoin'));
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="search-switcher-bar">
        <Spinner />
      </div>
    );
  }

  if (searches.length === 0) {
    return null;
  }

  return (
    <div className="search-switcher-bar" ref={ref}>
      <button
        ref={triggerRef}
        className="search-switcher-trigger"
        onClick={() => (open ? closeDropdown() : openDropdown())}
        onKeyDown={handleTriggerKeyDown}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className="search-switcher-label">{selectedSearch ? selectedSearch.name : t('app.searchSwitcher.selectSearch')}</span>
        {selectedSearch && (
          <Badge variant={STATUS_VARIANT[selectedSearch.status] ?? 'default'}>{selectedSearch.status}</Badge>
        )}
        <span className="search-switcher-chevron" aria-hidden="true">{open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</span>
      </button>
      {open && (
        <div className="search-switcher-dropdown" role="listbox" aria-label={t('app.searchSwitcher.ariaLabel')}>
          {searches.map((search, index) => (
            <button
              key={search.id}
              ref={(el) => {
                optionRefs.current[index] = el;
              }}
              role="option"
              aria-selected={search.id === selectedSearch?.id}
              className={`search-switcher-option ${search.id === selectedSearch?.id ? 'active' : ''} ${focusedIndex === index ? 'focused' : ''}`}
              onClick={() => {
                onSelect(search.id);
                closeDropdown();
              }}
              onKeyDown={(e) => handleOptionKeyDown(e, index)}
              tabIndex={focusedIndex === index ? 0 : -1}
            >
              <div className="search-option-main">
                <span className="search-option-name">{search.name}</span>
                <Badge variant={STATUS_VARIANT[search.status] ?? 'default'}>{search.status}</Badge>
              </div>
              <span className="search-option-meta">
                {t('app.searchSwitcher.member', { count: search.members.length })}
              </span>
            </button>
          ))}
          {onCreateSearch && (
            <button
              className="search-switcher-create"
              onClick={() => {
                onCreateSearch();
                closeDropdown();
              }}
            >
              {t('app.searchSwitcher.newSearch')}
            </button>
          )}
          {!showJoinInput ? (
            <button
              className="search-switcher-join"
              onClick={() => setShowJoinInput(true)}
            >
              {t('app.searchSwitcher.joinSearch')}
            </button>
          ) : (
            <div className="search-switcher-join-form">
              <form
                onSubmit={(e) => { e.preventDefault(); handleJoin(); }}
              >
                <input
                  className="input search-switcher-join-input"
                  type="text"
                  placeholder={t('app.searchSwitcher.inviteCodePlaceholder')}
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                  disabled={joining}
                  autoFocus
                />
                {joinError && <p className="search-switcher-join-error">{joinError}</p>}
                <button
                  className="btn btn-primary btn-sm"
                  type="submit"
                  disabled={joining || !inviteCode.trim()}
                  style={{ width: '100%' }}
                >
                  {joining ? t('app.searchSwitcher.joining') : t('app.searchSwitcher.join')}
                </button>
              </form>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
